import { ActionIcon, Flex } from "@mantine/core";
import { Tabs } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCollectionSummary } from "../../api/hooks/collectionSummary/useCollectionSummary.ts";
import { useCreateRiskMonitoringRemarks } from "../../api/hooks/customer/useCreateRiskMonitoringRemarks.ts";
import { useCustomer } from "../../api/hooks/customer/useCustomer.ts";
import { useRiskMonitoringRemarks } from "../../api/hooks/customer/useRiskMonitoringRemarks.ts";
import { useUpdateCustomer } from "../../api/hooks/customer/useUpdateCustomer.ts";
import { useUpdateRiskMonitoringRemarks } from "../../api/hooks/customer/useUpdateRiskMonitoringRemarks.ts";
import { useCollectors } from "../../api/hooks/user/useCollectors.ts";
import SvgChevronLeft from "../../components/Icons/ChevronLeft.tsx";
import LateFeeTab from "../../components/LateFeeTab/LateFeeTab.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import Preloader from "../../components/Preloader/Preloader.tsx";
import Title from "../../components/Title/Title.tsx";
import { AppRoute } from "../../constants.ts";
import {
  UpdateCustomerInput,
  UpdateCustomerRiskMonitoringRemarksInput,
} from "../../graphql/generated";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useTabFromUrl } from "../../hooks/useTabFromUrl.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { isBababosTenant } from "../../utils/user.ts";
import GiroManagementTable from "../ClikDataDetails/LimitInfo/GiroManagement/GiroManagementTable.tsx";
import TransactionHistoryList from "../ClikDataDetails/TransactionHistory/TransactionHistoryList.tsx";
import ActivityManagementCards from "./ActivityManagementCards/ActivityManagementCards.tsx";
import { useStyles } from "./ActivityManagementDetail.styles.ts";
import CollectionActivityTab from "./CollectionActivityTab/CollectionActivityTab.tsx";
import CollectionDocumentsTab from "./CollectionDocumentsTab/CollectionDocumentsTab.tsx";
import { getTabs, TabValue } from "./constants.tsx";
import ActivityManagementInvoicesTab from "./InvoicesTab/InvoicesTab.tsx";
import ContactDataTab from "./OdooContactDataTab/ContactDataTab.tsx";
import RestructuresTab from "./RestructuresTab/RestructuresTab.tsx";
import WaivesTab from "./WaivesTab/WaivesTab.tsx";

export default function ActivityManagementDetailPage() {
  const { classes } = useStyles();

  const { customerId } = useParams() as { customerId: string };
  const { user } = useCurrentUserContext();

  const navigate = useNavigate();

  const activeTab = useTabFromUrl({
    defaultTab: TabValue.OdooContactData,
    tabs: Object.values(TabValue),
  });

  const [
    { data: customerData, fetching: customerFetching, error },
    refreshCustomer,
  ] = useCustomer({
    id: customerId,
  });
  const customer = customerData?.customer;

  const [
    { data: summaryData, fetching: summaryFetching, error: summaryError },
    refreshCustomerSummary,
  ] = useCollectionSummary({ customerId });
  const collectionSummary = summaryData?.collectionSummary;

  const [
    {
      data: riskMonitoringRemarksData,
      fetching: fetchingRiskMonitoringRemarks,
    },
    refreshRiskMonitoringRemarks,
  ] = useRiskMonitoringRemarks({ customerId, pause: !isBababosTenant(user) });
  const riskMonitoringRemarks =
    riskMonitoringRemarksData?.customerRiskMonitoringRemarks;

  const [, createRiskMonitoringRemarks] = useMutationNotificationWrapper(
    useCreateRiskMonitoringRemarks(),
    {
      success: {
        message: "Risk Monitoring has been updated successfully.",
      },
    }
  );
  const [, updateRiskMonitoringRemarks] = useMutationNotificationWrapper(
    useUpdateRiskMonitoringRemarks(),
    {
      success: {
        message: "Risk Monitoring has been updated successfully.",
      },
    }
  );

  const [{ data: collectorsData, fetching: collectorsFetching }] =
    useCollectors();
  const collectors = collectorsData?.users.data;

  const [, updateCustomer] = useMutationNotificationWrapper(
    useUpdateCustomer(),
    {
      success: { message: "Customer has been updated." },
    }
  );

  const handleUpdateCustomer = useCallback(
    (value: Partial<UpdateCustomerInput>) => {
      if (!customer) {
        return;
      }
      updateCustomer({
        id: Number(customerId),
        input: {
          collectorAssignedId: value.collectorAssignedId
            ? Number(value.collectorAssignedId)
            : customer.collectorAssigned?.id,
          problemCategory: value.problemCategory ?? customer.problemCategory,
        },
      }).then((data) => {
        if (!data.error) {
          refreshCustomer();
          refreshCustomerSummary();
        }
      });
    },
    [
      customer,
      customerId,
      refreshCustomer,
      refreshCustomerSummary,
      updateCustomer,
    ]
  );

  const handleUpdateRiskMonitoringRemarks = useCallback(
    (input: UpdateCustomerRiskMonitoringRemarksInput) => {
      if (!customer?.id) {
        return;
      }
      const handler = riskMonitoringRemarks?.id
        ? updateRiskMonitoringRemarks({
            id: riskMonitoringRemarks.id,
            input,
          })
        : createRiskMonitoringRemarks({
            input: { ...input, customerId: customer.id },
          });
      handler.then(refreshRiskMonitoringRemarks);
    },
    [
      customer?.id,
      riskMonitoringRemarks?.id,
      updateRiskMonitoringRemarks,
      createRiskMonitoringRemarks,
      refreshRiskMonitoringRemarks,
    ]
  );

  const handleBack = () => navigate(AppRoute.ActivityManagement);

  if (error || summaryError) {
    navigate(AppRoute.ActivityManagement);
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <Layout>
      <Preloader loading={customerFetching} />
      {customer && collectionSummary && collectors && (
        <Flex direction="column" gap={{ base: 24, sm: 20 }}>
          <Flex direction="column" gap={16}>
            <Flex gap={20}>
              <ActionIcon size="m" variant="outlineGolden" onClick={handleBack}>
                <SvgChevronLeft />
              </ActionIcon>
              <Title className={classes.titleWrapper}>
                {customer.partnerCode
                  ? `${customer.name} - ${customer.partnerCode}`
                  : customer.name}
              </Title>
            </Flex>
            <ActivityManagementCards
              customer={{ data: customer, loading: customerFetching }}
              collectionSummary={{
                data: collectionSummary,
                loading: summaryFetching || collectorsFetching,
              }}
              riskMonitoring={{
                data: riskMonitoringRemarks,
                loading: fetchingRiskMonitoringRemarks,
              }}
              collectors={collectors}
              onChangeCustomer={handleUpdateCustomer}
              onChangeRiskMonitoring={handleUpdateRiskMonitoringRemarks}
            />
          </Flex>
          <Tabs
            value={activeTab}
            keepMounted={false}
            variant="outline"
            onTabChange={(value) => navigate(`?tab=${value}`)}
          >
            <Tabs.List>
              {getTabs(user).map((item, index) => {
                return (
                  <Tabs.Tab key={index} value={item.value}>
                    {item.label}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
            <Tabs.Panel value={TabValue.OdooContactData}>
              <ContactDataTab />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.Invoices}>
              <ActivityManagementInvoicesTab />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.CollectionActivity}>
              <CollectionActivityTab />
            </Tabs.Panel>
            <Tabs.Panel
              value={TabValue.CollectionDocument}
              hidden={!isBababosTenant(user)}
            >
              <CollectionDocumentsTab />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.Restructure}>
              <RestructuresTab />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.Waive}>
              <WaivesTab />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.LateFee}>
              <LateFeeTab customerId={Number(customerId)} />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.Giro}>
              <GiroManagementTable company={customer} canEdit={false} />
            </Tabs.Panel>
            <Tabs.Panel value={TabValue.Transactions}>
              <TransactionHistoryList customer={customer} canEdit={false} />
            </Tabs.Panel>
          </Tabs>
        </Flex>
      )}
    </Layout>
  );
}
